import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Compass, ExternalLink, Anchor } from "lucide-react";
import ListenButton from "../components/ListenButton";
import { OUR_COCHIN as A } from "../content/ourCochin";

/* Special editorial article — linked from the Kochi travel guide. Audio enabled via ListenButton. */
export default function OurCochin() {
  useEffect(() => {
    document.title = `${A.heading} — TravelerPulse.pro`;
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="our-cochin-article">
      <div className="bg-gradient-to-br from-emerald-800 to-teal-900 text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-12">
          <nav className="text-xs text-white/70 flex items-center gap-1 mb-4 flex-wrap">
            <Link to="/explore/india" className="hover:underline">India</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/explore/india/kerala" className="hover:underline">Kerala</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/explore/india/kerala/kochi" className="hover:underline" data-testid="our-cochin-back-guide">Kochi Travel Guide</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/specials" className="hover:underline" data-testid="our-cochin-specials-link">TravelerPulse Specials</Link>
          </nav>
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-300 flex items-center gap-1.5 mb-2"><Anchor className="w-4 h-4" /> A TravelerPulse Special</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl leading-tight">{A.title}</h1>
          <p className="text-lg text-emerald-100 mt-2">{A.subtitle}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-6">
          <ListenButton targetId="our-cochin-body" label="Listen to this article" />
          <Link to="/explore/india/kerala/kochi" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="our-cochin-guide-link">
            <Compass className="w-3.5 h-3.5" /> Kochi Travel Guide →
          </Link>
        </div>

        <div id="our-cochin-body">
          <h2 className="font-heading font-900 text-2xl md:text-3xl mb-4">{A.heading}</h2>
          <div className="text-base leading-relaxed whitespace-pre-line mb-10 first-letter:text-5xl first-letter:font-900 first-letter:font-heading first-letter:float-left first-letter:mr-2 first-letter:leading-none first-letter:text-emerald-700">
            {A.intro}
          </div>
          {A.sections.map((s) => (
            <section key={s.h} className="mb-10">
              <h2 className="font-heading font-900 text-xl md:text-2xl border-b-2 border-emerald-600 pb-2 mb-3">{s.h}</h2>
              <div className="text-base leading-relaxed whitespace-pre-line">{s.t}</div>
            </section>
          ))}
        </div>

        <section className="border border-zinc-300 bg-emerald-50/50 p-5 mb-8" data-testid="our-cochin-links">
          <h2 className="font-heading font-900 text-lg mb-3">Explore Further</h2>
          <p className="text-xs text-muted-foreground mb-3">For readers who would like to separate history from legend and explore the region in greater depth:</p>
          <ul className="space-y-2">
            {A.links.map((l) => (
              <li key={l.url}>
                <a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5">
                  <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l.label}
                </a>
              </li>
            ))}
          </ul>
        </section>

        <Link to="/explore/india/kerala/kochi" data-testid="our-cochin-footer-guide"
          className="flex items-center justify-between gap-3 border border-zinc-300 bg-white px-4 py-3 hover:border-emerald-600 transition-colors mb-8">
          <span className="text-sm font-700">Plan your visit — Kochi Travel Guide</span>
          <ChevronRight className="w-4 h-4 shrink-0" />
        </Link>

        <p className="text-[11px] text-muted-foreground">
          Presented by <a href="https://travelerpulse.pro" target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline">TravelerPulse.pro™</a> — folklore is shared as folklore; verify time-sensitive facts on official sites.
        </p>
      </div>
    </div>
  );
}
